import React, { useState, useEffect, useRef } from 'react';
import BackButton from '../components/BackButton';

const Login = ({ onLogin, onBack }) => {
    const [step, setStep] = useState(1);
    const [contact, setContact] = useState('');
    const [timer, setTimer] = useState(30);
    const [otp, setOtp] = useState(new Array(6).fill(""));
    const [canResend, setCanResend] = useState(false);
    const otpInputRefs = useRef([]);

    useEffect(() => {
        let interval;
        if (step === 2 && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        } else if (timer === 0) {
            setCanResend(true);
        }
        return () => clearInterval(interval);
    }, [step, timer]);

    const handleSendOtp = (e) => {
        e.preventDefault();
        if (contact) {
            setStep(2);
            setTimer(30);
            setCanResend(false);
            setTimeout(() => {
                if (otpInputRefs.current[0]) otpInputRefs.current[0].focus();
            }, 100);
        }
    };

    const handleResend = () => {
        setTimer(30);
        setCanResend(false);
        setOtp(new Array(6).fill(""));
        alert('OTP Resent!');
    };

    const handleOtpChange = (element, index) => {
        if (isNaN(element.value)) return false;
        const newOtp = [...otp];
        newOtp[index] = element.value;
        setOtp(newOtp);

        if (element.value && index < 5) {
            otpInputRefs.current[index + 1].focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            otpInputRefs.current[index - 1].focus();
        }
    };

    return (
        <div className="bg-welcome-bg-light dark:bg-welcome-bg-dark font-body min-h-screen flex items-center justify-center p-4 selection:bg-welcome-secondary selection:text-welcome-primary transition-colors duration-300">
            {/* Decorative Background Elements */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
                <div className="absolute -top-20 -left-20 w-96 h-96 bg-welcome-secondary/20 dark:bg-welcome-primary/20 rounded-full blur-3xl"></div>
                <div className="absolute top-1/2 -right-20 w-80 h-80 bg-green-200/30 dark:bg-green-900/20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-1/4 w-full h-64 bg-gradient-to-t from-white/40 to-transparent dark:from-black/20"></div>
            </div>

            <div className="relative z-10 w-full max-w-5xl flex flex-col lg:flex-row bg-welcome-card-light dark:bg-welcome-card-dark rounded-2xl shadow-2xl overflow-hidden min-h-[600px] mx-4 lg:mx-0">
                {/* Left Section - Form */}
                <div className="w-full lg:w-1/2 p-6 md:p-12 flex flex-col justify-center relative">
                    <div className="absolute top-6 left-6 md:top-8 md:left-8 flex items-center gap-4">
                        <BackButton onClick={onBack} title="Back to Role Selection" className="!bg-gray-100 dark:!bg-slate-800" />
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 md:w-10 md:h-10 bg-welcome-primary rounded-lg flex items-center justify-center text-white font-bold text-lg md:text-xl">
                                H
                            </div>
                            <span className="text-lg md:text-xl font-bold text-gray-900 dark:text-white tracking-tight">Himigo</span>
                        </div>
                    </div>

                    <div className="mt-16 lg:mt-0 max-w-md mx-auto w-full">
                        <div className="mb-6 md:mb-8">
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">Welcome Back! 👋</h1>
                            <p className="text-sm md:text-base text-gray-500 dark:text-gray-400 font-medium">Please login to access your learning dashboard.</p>
                        </div>

                        {/* Step 1: Input Contact */}
                        {step === 1 && (
                            <div className="transition-all duration-300">
                                <form onSubmit={handleSendOtp}>
                                    <div className="mb-6">
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2" htmlFor="contact">
                                            Email or Mobile Number
                                        </label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                                <span className="material-icons-round text-xl">perm_identity</span>
                                            </span>
                                            <input
                                                className="w-full pl-10 pr-4 py-3 md:py-3.5 rounded-xl border border-welcome-input-border-light dark:border-welcome-input-border-dark bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-welcome-secondary/50 focus:border-welcome-primary dark:focus:border-welcome-secondary outline-none transition-all placeholder-gray-400 text-sm md:text-base"
                                                id="contact"
                                                type="text"
                                                placeholder="student@example.com or +1234567890"
                                                required
                                                value={contact}
                                                onChange={(e) => setContact(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <button className="w-full bg-welcome-primary hover:bg-welcome-primary-hover text-white font-semibold py-3 md:py-3.5 rounded-xl transition-all transform active:scale-[0.98] shadow-lg shadow-green-900/10 flex items-center justify-center gap-2 group text-sm md:text-base" type="submit">
                                        Send OTP
                                        <span className="material-icons-round group-hover:translate-x-1 transition-transform text-lg">arrow_forward</span>
                                    </button>
                                </form>

                                <div className="mt-8 flex items-center justify-center gap-4">
                                    <div className="h-px bg-gray-200 dark:bg-gray-700 flex-1"></div>
                                    <span className="text-xs text-gray-400 uppercase font-medium">Or continue with</span>
                                    <div className="h-px bg-gray-200 dark:bg-gray-700 flex-1"></div>
                                </div>

                                <div className="mt-6">
                                    <button className="w-full flex items-center justify-center gap-3 py-3 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-all shadow-sm hover:shadow-md active:scale-[0.98]">
                                        <img alt="Google" className="w-5 h-5" src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" />
                                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Continue with Google</span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 2: OTP Verification */}
                        {step === 2 && (
                            <div className="transition-all duration-300">
                                <div className="mb-6">
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                        Enter Verification Code
                                    </label>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 font-medium">
                                        We've sent a 6-digit code to <span className="font-bold text-gray-900 dark:text-white uppercase tracking-tight">{contact}</span>.
                                    </p>
                                    <div className="flex justify-between gap-2 mb-4">
                                        {otp.map((digit, index) => (
                                            <input
                                                key={index}
                                                maxLength="1"
                                                type="text"
                                                className="otp-input w-12 h-14 text-center text-xl font-bold rounded-xl border border-welcome-input-border-light dark:border-welcome-input-border-dark bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-welcome-secondary/50 focus:border-welcome-primary outline-none transition-all"
                                                value={digit}
                                                ref={el => otpInputRefs.current[index] = el}
                                                onChange={e => handleOtpChange(e.target, index)}
                                                onKeyDown={e => handleKeyDown(e, index)}
                                            />
                                        ))}
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1 font-medium">
                                            <span className="material-icons-round text-base">timer</span>
                                            00:{timer < 10 ? `0${timer}` : timer}
                                        </span>
                                        <button
                                            className={`font-bold transition-colors ${canResend ? 'text-welcome-primary hover:underline' : 'text-welcome-primary opacity-50 cursor-not-allowed'}`}
                                            disabled={!canResend}
                                            onClick={handleResend}
                                        >
                                            Resend OTP
                                        </button>
                                    </div>
                                </div>
                                <button
                                    className="w-full bg-welcome-primary hover:bg-welcome-primary-hover text-white font-bold py-3.5 rounded-xl transition-all transform active:scale-[0.98] shadow-lg shadow-green-900/10 mb-4"
                                    onClick={onLogin}
                                >
                                    Verify &amp; Login
                                </button>
                                <button className="w-full text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white text-sm font-bold transition-colors" onClick={() => setStep(1)}>
                                    Change Mobile/Email
                                </button>
                            </div>
                        )}

                        <div className="mt-8 text-center">
                            <p className="text-xs text-gray-400 dark:text-gray-500 font-medium">
                                By logging in, you agree to our <a className="underline hover:text-welcome-primary" href="#">Terms of Service</a> and <a className="underline hover:text-welcome-primary" href="#">Privacy Policy</a>.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Right Section - Visual/Hero */}
                <div className="hidden lg:block w-1/2 relative bg-welcome-primary overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-welcome-primary/90 to-welcome-primary/40 z-10 flex flex-col justify-end p-12 text-white">
                        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 shadow-xl mb-6 transform transition-transform hover:-translate-y-1 duration-500">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-12 h-12 rounded-full bg-welcome-secondary/20 border border-welcome-secondary flex items-center justify-center text-welcome-secondary">
                                    <span className="material-icons-round text-2xl">school</span>
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg">Keep Learning</h3>
                                    <p className="text-welcome-secondary text-sm font-medium">Consistent daily progress</p>
                                </div>
                            </div>
                            <div className="w-full bg-white/20 h-2 rounded-full overflow-hidden">
                                <div className="bg-welcome-secondary w-3/4 h-full rounded-full"></div>
                            </div>
                            <div className="flex justify-between mt-2 text-xs text-gray-100 font-medium">
                                <span>Course Progress</span>
                                <span>75%</span>
                            </div>
                        </div>
                        <h2 className="text-3xl font-bold mb-4 leading-tight">Upgrade Your Training Workflow</h2>
                        <p className="text-gray-100 text-lg opacity-90 font-medium leading-relaxed">Join thousands of students achieving their goals with our intuitive learning platform.</p>
                    </div>
                    <img
                        alt="Background"
                        className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-60"
                        src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=2070&auto=format&fit=crop"
                    />
                    <div className="absolute top-10 right-10 w-24 h-24 border-4 border-welcome-secondary/30 rounded-full z-0"></div>
                </div>
            </div>

            {/* Dark Mode Toggle */}
            <div className="fixed bottom-6 right-6 z-50">
                <button
                    className="w-12 h-12 rounded-full bg-white dark:bg-welcome-card-dark text-gray-900 dark:text-white shadow-xl flex items-center justify-center hover:scale-110 active:scale-90 transition-all border border-gray-100 dark:border-gray-800"
                    onClick={() => document.documentElement.classList.toggle('dark')}
                >
                    <span className="material-icons-round dark:hidden text-emerald-700">dark_mode</span>
                    <span className="material-icons-round hidden dark:block text-emerald-400">light_mode</span>
                </button>
            </div>
        </div>
    );
};

export default Login;
